import { useCallback, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';

import axiosInstance, { API_ENDPOINTS, fetcher } from 'src/utils/axios';

// ----------------------------------------------------------------------

// Careers to-dos (server/services/career-todos). Every change answers with the
// student's to-dos, which go straight into the cache; "My to-dos" is refetched,
// since a change may add to or take from it.

export function useGetStudentTodos(asn) {
  const { data, isLoading, error } = useSWR(asn ? API_ENDPOINTS.student.todos(asn) : null, fetcher);
  return useMemo(
    () => ({ todos: data?.todos || [], todosLoading: isLoading, todosError: error }),
    [data, isLoading, error]
  );
}

export function useCareerTodoActions(asn) {
  const [saving, setSaving] = useState(false);

  const run = useCallback(
    async (request) => {
      setSaving(true);
      try {
        const { data } = await request();
        if (Array.isArray(data?.todos)) {
          mutate(API_ENDPOINTS.student.todos(asn), { todos: data.todos }, { revalidate: false });
        }
        mutate(API_ENDPOINTS.career.myTodos);
        return data;
      } finally {
        setSaving(false);
      }
    },
    [asn]
  );

  return useMemo(
    () => ({
      saving,
      createTodo: (input) => run(() => axiosInstance.post(API_ENDPOINTS.student.todos(asn), input)),
      updateTodo: (id, patch) => run(() => axiosInstance.put(API_ENDPOINTS.student.todo(asn, id), patch)),
      deleteTodo: (id) => run(() => axiosInstance.delete(API_ENDPOINTS.student.todo(asn, id))),
    }),
    [asn, run, saving]
  );
}

/** Who a to-do can be assigned to; asked only once the picker is needed. */
export function useGetTodoAssignees(enabled = true) {
  const { data, isLoading } = useSWR(enabled ? API_ENDPOINTS.career.todoAssignees : null, fetcher, {
    revalidateOnFocus: false,
  });
  return useMemo(() => ({ assignees: data?.assignees || [], assigneesLoading: isLoading }), [data, isLoading]);
}

/** The signed-in user's open to-dos across students. */
export function useGetMyTodos() {
  const { data, isLoading, error } = useSWR(API_ENDPOINTS.career.myTodos, fetcher);
  return useMemo(
    () => ({ myTodos: data?.todos || [], myTodosLoading: isLoading, myTodosError: error }),
    [data, isLoading, error]
  );
}
